import { InsuranceIndicationStatus, Vendor } from '@/models/insuranceTypes'
import { type CarrierCard, taglineFor } from '@/routes/Public/carrierCard'

const LOGO_BASE = 'https://static.cai.insure/carriers'

/**
 * Static "example results" shown before the visitor submits a DOT — illustrative
 * round bands, clearly marked by the EXAMPLES stamp so they read as a sample of
 * what the tool returns, never as a real indication. The real result cards come
 * from live indications/estimates once a DOT is confirmed (see ResultsPanel).
 */
function makeCard(
  partial: Partial<CarrierCard> & Pick<CarrierCard, 'id' | 'vendor' | 'vendorName'>
): CarrierCard {
  return {
    status: InsuranceIndicationStatus.GENERATED,
    iconUrl: `${LOGO_BASE}/${partial.id}-icon.png`,
    iconAlt: partial.vendorName,
    carrierName: null,
    truckCount: null,
    perTruckLow: null,
    perTruckHigh: null,
    fleetLow: null,
    fleetHigh: null,
    carrierNote: null,
    carrierPublicNote: null,
    declineReason: null,
    isCurrentInsurer: false,
    tagline: null,
    ...partial,
  }
}

export const EXAMPLE_CARDS: CarrierCard[] = [
  makeCard({
    id: 'geico',
    vendor: Vendor.GEICO,
    vendorName: 'GEICO',
    perTruckLow: 9000,
    perTruckHigh: 14000,
  }),
  makeCard({
    id: 'progressive',
    vendor: Vendor.ROCKLAKE,
    vendorName: 'Progressive',
    carrierName: 'Progressive',
    perTruckLow: 10000,
    perTruckHigh: 16000,
  }),
  makeCard({
    id: 'coverwhale',
    vendor: Vendor.COVERWHALE,
    vendorName: 'Cover Whale',
    perTruckLow: 11000,
    perTruckHigh: 18000,
  }),
  makeCard({
    id: 'corgi',
    vendor: Vendor.CORGI,
    vendorName: 'Corgi',
    perTruckLow: 12000,
    perTruckHigh: 19000,
  }),
]

/** Range-bar bounds + per-card taglines across a card set, mirroring the real flow. */
export function withRangeMeta(cards: CarrierCard[]): {
  cards: CarrierCard[]
  rangeBounds: { min: number; max: number } | null
} {
  const lows = cards.map((c) => c.perTruckLow).filter((v): v is number => v != null)
  const bestLow = lows.length > 0 ? Math.min(...lows) : null
  for (const card of cards) card.tagline = taglineFor(card, bestLow)
  const rangeBounds =
    bestLow != null
      ? { min: bestLow, max: Math.max(...cards.map((c) => c.perTruckHigh ?? c.perTruckLow ?? 0)) }
      : null
  return { cards, rangeBounds }
}
